/**
 * GTM DataLayer - Theme JS
 * File: gtm-datalayer-theme.js (upload to theme Assets)
 *
 * Purpose: Storefront-side GTM support.
 *   - Add/remove to cart: Intercepts /cart/add.js and /cart/change fetch/XHR calls
 *     and pushes events to window.dataLayer via /cart.js (Avis-safe).
 *     Avis Product Options causes the server to return {"sections":{...}} for cart
 *     mutations — stripping all line item data from Shopify Customer Events. We
 *     bypass this by fetching /cart.js (which Avis never intercepts) for add_to_cart
 *     and by snapshotting the cached cart before the request for remove_from_cart.
 *   - Pixel message bridge: receives postMessage events from the Shopify Customer
 *     Events pixel (cross-origin sandbox) and forwards them into window.dataLayer
 *     for the GTM instance loaded in theme.liquid.
 *   - Click tracking: publishes custom Shopify analytics events that the pixel
 *     subscribes to and forwards to the dataLayer.
 *
 * Requires: GTMConfig defined in theme.liquid before this script loads.
 *
 * IMPORTANT: Must be loaded synchronously (no defer, no async) so the fetch
 *            interceptor and message listener are registered before GTM fires.
 *            The fetch interceptor must install before Avis's fetch wrapper —
 *            this is guaranteed by synchronous loading in <head>.
 */

(function () {
   /* ===========================================================================
     CONFIG
     trackAddToCart    — set true when Avis (or similar) strips /cart/add.js
                         responses. Disable in the pixel (trackAddToCart: false)
                         when this is enabled.
     trackRemoveFromCart — same for /cart/change responses.
     Set both to false on stores without Avis and let the pixel handle events.
  =========================================================================== */

   var GTMConfig = window.GTMConfig || {};
   var debugMode = GTMConfig.debugMode || false;
   var trackAddToCart = GTMConfig.trackAddToCart || false;
   var trackRemoveFromCart = GTMConfig.trackRemoveFromCart || false;
   var shopName = (GTMConfig.store && GTMConfig.store.name) || '';
   var shopCurrency = (GTMConfig.store && GTMConfig.store.currency) || '';

   /* ===========================================================================
     DEBUG LOGGING
  =========================================================================== */

   function themeLog(message, data) {
      if (!debugMode) return;
      if (data !== undefined) {
         console.log('[THEME] ' + message, data);
      } else {
         console.log('[THEME] ' + message);
      }
   }

   /* ===========================================================================
     ADD / REMOVE FROM CART — THEME-SIDE HANDLERS
     Avis Product Options causes the Shopify server to return {"sections":{...}}
     for /cart/add.js and /cart/change when its session is active (regular
     browser with Avis cookies). This strips all line item data from the
     product_added_to_cart and product_removed_from_cart Customer Events.

     Fix for add_to_cart:
       Capture variant ID from request body → after response → fetch /cart.js
       (Avis never intercepts /cart.js) → push add_to_cart from full cart data.

     Fix for remove_from_cart:
       Snapshot the line item from cachedCart BEFORE the remove request →
       after response → push remove_from_cart from the snapshot → refresh cache.
       (After remove, the item is gone from cart so we can't look it up post-hoc.)
  =========================================================================== */

   var CART_ADD_PATTERN = /\/cart\/add(\.js|\.json|\/|$|\?)/;
   var CART_CHANGE_PATTERN = /\/cart\/(change|update)(\.js|\.json|\/|$|\?)/;

   var cachedCart = null;
   var pendingRemove = null; // snapshot captured before remove request

   function isCartAddUrl(url) {
      var s = typeof url === 'string' ? url : url && url.url ? url.url : '';
      return CART_ADD_PATTERN.test(s);
   }

   function isCartChangeUrl(url) {
      var s = typeof url === 'string' ? url : url && url.url ? url.url : '';
      return CART_CHANGE_PATTERN.test(s);
   }

   // --- Body parsers ---

   function extractVariantIdFromBody(body) {
      try {
         if (!body) return null;
         if (typeof body === 'string') {
            try {
               var j = JSON.parse(body);
               return j.id || j.variant_id || (j.items && j.items[0] && j.items[0].id) || null;
            } catch (e) {}
            return new URLSearchParams(body).get('id') || new URLSearchParams(body).get('variant_id') || null;
         }
         if (body instanceof URLSearchParams) return body.get('id') || body.get('variant_id') || null;
         if (body instanceof FormData) return body.get('id') || body.get('variant_id') || null;
      } catch (e) {}
      return null;
   }

   function extractQuantityFromBody(body) {
      try {
         if (!body) return 1;
         if (typeof body === 'string') {
            try {
               var j = JSON.parse(body);
               return parseInt(j.quantity || (j.items && j.items[0] && j.items[0].quantity), 10) || 1;
            } catch (e) {}
            return parseInt(new URLSearchParams(body).get('quantity'), 10) || 1;
         }
         if (body instanceof URLSearchParams) return parseInt(body.get('quantity'), 10) || 1;
         if (body instanceof FormData) return parseInt(body.get('quantity'), 10) || 1;
      } catch (e) {}
      return 1;
   }

   /**
    * Parses a cart/change request body.
    * Returns { line, id, quantity } — line is 1-based index, id is line item key.
    */
   function extractChangeInfoFromBody(body) {
      try {
         var parsed = {};
         if (!body) return parsed;
         if (typeof body === 'string') {
            try {
               parsed = JSON.parse(body);
            } catch (e) {
               var p = new URLSearchParams(body);
               if (p.get('line') !== null) parsed.line = parseInt(p.get('line'), 10);
               if (p.get('id') !== null) parsed.id = p.get('id');
               if (p.get('quantity') !== null) parsed.quantity = parseInt(p.get('quantity'), 10);
            }
         } else if (body instanceof URLSearchParams) {
            if (body.get('line') !== null) parsed.line = parseInt(body.get('line'), 10);
            if (body.get('id') !== null) parsed.id = body.get('id');
            if (body.get('quantity') !== null) parsed.quantity = parseInt(body.get('quantity'), 10);
         } else if (body instanceof FormData) {
            if (body.get('line') !== null) parsed.line = parseInt(body.get('line'), 10);
            if (body.get('id') !== null) parsed.id = body.get('id');
            if (body.get('quantity') !== null) parsed.quantity = parseInt(body.get('quantity'), 10);
         }
         return parsed;
      } catch (e) {}
      return {};
   }

   // --- Cart cache ---

   function fetchAndCacheCart(callback) {
      fetch('/cart.js')
         .then(function (r) {
            return r.json();
         })
         .then(function (cart) {
            cachedCart = cart;
            if (callback) callback(cart);
         })
         .catch(function (e) {
            themeLog('fetchAndCacheCart error', e);
         });
   }

   // --- Item builder ---

   function buildItemFromCartLine(line, quantity) {
      var discountPerUnit = line.quantity > 0 ? (line.total_discount / 100) / line.quantity : 0;
      var price = Math.max((line.price / 100) - discountPerUnit, 0);
      return {
         item_id: String(line.product_id || ''),
         item_name: line.product_title || '',
         affiliation: shopName,
         item_brand: line.vendor || '',
         item_category: line.product_type || '',
         item_variant: line.variant_title !== 'Default Title' ? line.variant_title || undefined : undefined,
         item_sku: line.sku || undefined,
         price: price,
         quantity: quantity,
      };
   }

   // --- Event pushers ---

   function pushAddToCart(cart, variantId, quantity) {
      var variantIdNum = parseInt(variantId, 10);
      var line = null;
      for (var i = cart.items.length - 1; i >= 0; i--) {
         if (cart.items[i].variant_id === variantIdNum) {
            line = cart.items[i];
            break;
         }
      }
      if (!line) {
         themeLog('add_to_cart: variant not found in cart', { variantId: variantId });
         return;
      }
      var item = buildItemFromCartLine(line, quantity);
      var value = item.price * quantity;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
         event: 'add_to_cart',
         event_source: 'theme',
         page_location: window.location.href,
         ecommerce: {
            currency: shopCurrency || cart.currency,
            value: value,
            items: [item],
         },
      });
      themeLog('add_to_cart pushed', { variantId: variantId, quantity: quantity, value: value });
   }

   function pushRemoveFromCart(snapshotLine, removedQuantity) {
      var item = buildItemFromCartLine(snapshotLine, removedQuantity);
      var value = item.price * removedQuantity;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
         event: 'remove_from_cart',
         event_source: 'theme',
         page_location: window.location.href,
         ecommerce: {
            currency: (cachedCart && cachedCart.currency) || shopCurrency,
            value: value,
            items: [item],
         },
      });
      themeLog('remove_from_cart pushed', { variantId: snapshotLine.variant_id, quantity: removedQuantity, value: value });
   }

   /**
    * Snapshots the line item from cachedCart before a cart/change request.
    * Returns { line: cartItemObject, removedQty: number } or null.
    */
   function capturePendingRemove(changeInfo) {
      if (!cachedCart || !cachedCart.items) return null;
      var cartLine = null;

      if (changeInfo.line !== undefined && !isNaN(changeInfo.line)) {
         cartLine = cachedCart.items[changeInfo.line - 1] || null;
      } else if (changeInfo.id !== undefined) {
         for (var i = 0; i < cachedCart.items.length; i++) {
            if (String(cachedCart.items[i].key) === String(changeInfo.id) || String(cachedCart.items[i].variant_id) === String(changeInfo.id)) {
               cartLine = cachedCart.items[i];
               break;
            }
         }
      }

      if (!cartLine) return null;

      var newQty = changeInfo.quantity !== undefined ? changeInfo.quantity : 0;
      var oldQty = cartLine.quantity;
      var removedQty = newQty === 0 ? oldQty : Math.max(oldQty - newQty, 0);

      if (removedQty <= 0) return null; // no removal, skip

      return { line: cartLine, removedQty: removedQty };
   }

   // --- Fetch interceptor ---

   (function () {
      if (!trackAddToCart && !trackRemoveFromCart) return;
      var originalFetch = window.fetch;
      if (typeof originalFetch !== 'function') return;

      window.fetch = function (input, init) {
         var url = input && typeof input === 'object' && input.url ? input.url : String(input || '');
         var body = init && init.body;

         if (trackAddToCart && isCartAddUrl(url)) {
            var variantId = extractVariantIdFromBody(body);
            var quantity = extractQuantityFromBody(body);
            themeLog('cart/add fetch intercepted', { variantId: variantId, quantity: quantity });
            return originalFetch.apply(this, arguments).then(function (response) {
               if (variantId) {
                  fetch('/cart.js')
                     .then(function (r) {
                        return r.json();
                     })
                     .then(function (cart) {
                        cachedCart = cart;
                        pushAddToCart(cart, variantId, quantity);
                     })
                     .catch(function (e) {
                        themeLog('add_to_cart: /cart.js error', e);
                     });
               }
               return response;
            });
         }

         if (trackRemoveFromCart && isCartChangeUrl(url)) {
            var changeInfo = extractChangeInfoFromBody(body);
            var pending = capturePendingRemove(changeInfo);
            themeLog('cart/change fetch intercepted', { changeInfo: changeInfo, pending: pending });
            return originalFetch.apply(this, arguments).then(function (response) {
               if (pending) pushRemoveFromCart(pending.line, pending.removedQty);
               fetchAndCacheCart(); // refresh cache after any cart mutation
               return response;
            });
         }

         return originalFetch.apply(this, arguments);
      };
   })();

   // --- XHR interceptor (fallback) ---

   (function () {
      if (!trackAddToCart && !trackRemoveFromCart) return;
      var OriginalXHR = window.XMLHttpRequest;
      if (!OriginalXHR || !OriginalXHR.prototype) return;
      var proto = OriginalXHR.prototype;
      var origOpen = proto.open;
      var origSend = proto.send;

      proto.open = function (method, url) {
         this._gtmUrl = url;
         return origOpen.apply(this, arguments);
      };

      proto.send = function (body) {
         var url = this._gtmUrl || '';
         var isAdd = trackAddToCart && isCartAddUrl(url);
         var isChange = trackRemoveFromCart && isCartChangeUrl(url);

         if (isAdd || isChange) {
            var variantId = isAdd ? extractVariantIdFromBody(body) : null;
            var quantity = isAdd ? extractQuantityFromBody(body) : null;
            var changeInfo = isChange ? extractChangeInfoFromBody(body) : null;
            var pending = isChange ? capturePendingRemove(changeInfo) : null;
            var xhr = this;
            themeLog('cart XHR intercepted', { url: url, variantId: variantId, changeInfo: changeInfo });

            // Use addEventListener — non-destructive, doesn't conflict with Avis's onreadystatechange wrapper
            xhr.addEventListener('load', function () {
               if (xhr.status < 200 || xhr.status >= 300) return;
               if (isAdd && variantId) {
                  fetch('/cart.js')
                     .then(function (r) { return r.json(); })
                     .then(function (cart) {
                        cachedCart = cart;
                        pushAddToCart(cart, variantId, quantity);
                     });
               }
               if (isChange) {
                  if (pending) pushRemoveFromCart(pending.line, pending.removedQty);
                  fetchAndCacheCart();
               }
            });
            xhr.addEventListener('error', function () {
               themeLog('cart XHR error', { url: url });
            });
         }
         return origSend.apply(this, arguments);
      };
   })();

   // Pre-fetch cart on page load so cachedCart is ready for the first remove
   if (trackRemoveFromCart) fetchAndCacheCart();

   /* ===========================================================================
     CLICK TRACKING
     Publishes custom Shopify analytics events that the custom pixel subscribes to.
     Guard: Shopify.analytics may not be defined if Shopify's JS hasn't loaded yet.
  =========================================================================== */

   /**
    * Returns the CSS path of a DOM element (for click tracking).
    * Borrowed from Simo Ahava: https://www.simoahava.com/analytics/create-css-path-variable-for-click-element/
    */
   function getClickElementPath(element) {
      if (!(element instanceof Node)) return '';
      var elList = [];
      var el = element;
      do {
         if (el instanceof Element) {
            var classString = el.classList ? [].slice.call(el.classList).join('.') : '';
            var elementName = (el.tagName ? el.tagName.toLowerCase() : '') + (classString ? '.' + classString : '') + (el.id ? '#' + el.id : '');
            if (elementName) elList.unshift(elementName);
         }
         el = el.parentNode;
      } while (el != null);
      var objString = element.toString().match(/\[object (\w+)\]/);
      var elementType = objString ? objString[1] : element.toString();
      var cssString = elList.join(' > ');
      return cssString ? elementType + ': ' + cssString : elementType;
   }

   window.addEventListener('click', function (e) {
      if (!window.Shopify || !Shopify.analytics) return;

      // className on SVG elements returns SVGAnimatedString, not a plain string
      var targetClasses = typeof e.target.className === 'string' ? e.target.className : '';

      var clickData = {
         click_element: getClickElementPath(e.target) || '',
         click_id: e.target.id || '',
         click_classes: targetClasses,
         click_text: e.target.innerText || '',
         click_target: e.target.target || '',
         click_url: e.target.href || '',
      };

      Shopify.analytics.publish('custom_click', clickData);

      var closestLink = e.target.closest('a');
      if (closestLink) {
         var linkClasses = typeof closestLink.className === 'string' ? closestLink.className : '';
         var linkData = {
            click_element: getClickElementPath(closestLink) || '',
            click_id: closestLink.id || '',
            click_classes: linkClasses,
            click_text: closestLink.innerText || '',
            click_target: closestLink.target || '',
            click_url: closestLink.href || '',
         };
         Shopify.analytics.publish('custom_link_click', linkData);
      }
   });

   /* ===========================================================================
     PIXEL MESSAGE BRIDGE
     Receives postMessage events from the Shopify custom pixel sandbox and
     forwards them into window.dataLayer for the single GTM instance.
     The pixel cannot access window.parent properties (cross-origin sandbox),
     but window.parent.postMessage() is allowed — it is a method call, not a
     property read. The 'gtm:datalayer' type namespaces these messages so
     unrelated iframe messages are ignored.
  =========================================================================== */

   window.addEventListener('message', function (event) {
      if (!event.data || event.data.type !== 'gtm:datalayer') return;
      window.dataLayer = window.dataLayer || [];
      if (event.data.flush) {
         window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(event.data.payload);
      themeLog('forwarded pixel event → ' + (event.data.payload && event.data.payload.event), event.data.payload);
   });
})();
